import {
  statsAdminControllerClientCryptors,
  statsAdminControllerCollections,
  statsAdminControllerComparison,
  statsAdminControllerNetwork,
  statsAdminControllerPeriod,
  statsAdminControllerQrs,
  statsAdminControllerSnapshot,
  unwrapApiResponse,
  type ClientCryptorCollectionEntity,
  type ComparisonKpis,
  type NetworkKpis,
  type PeriodKpis,
  type QrOptionEntity,
  type SnapshotKpis,
} from "@cryptors/api-client";
import { useQuery } from "@tanstack/react-query";

import { getCurrentUserKey } from "@/providers/authProvider";

const getSnapshotKpis = async (): Promise<SnapshotKpis> =>
  unwrapApiResponse(
    statsAdminControllerSnapshot<true>({
      throwOnError: true,
    }),
  );

export const useGetSnapshotKpis = (enabled = true) =>
  useQuery({
    queryKey: ["stats", "snapshot", getCurrentUserKey()],
    queryFn: () => getSnapshotKpis(),
    staleTime: 60_000,
    enabled,
    retry: false,
  });

const getNetworkKpis = async (): Promise<NetworkKpis> =>
  unwrapApiResponse(
    statsAdminControllerNetwork<true>({
      throwOnError: true,
    }),
  );

export const useGetNetworkKpis = (enabled = true) =>
  useQuery({
    queryKey: ["stats", "network", getCurrentUserKey()],
    queryFn: getNetworkKpis,
    staleTime: 60_000,
    enabled,
  });

interface CollectionOption {
  id: number;
  name: string;
  launchDate: string | null;
}

const getDashboardCollections = async (): Promise<CollectionOption[]> =>
  unwrapApiResponse(
    statsAdminControllerCollections<true>({
      throwOnError: true,
    }),
  );

export const useGetDashboardCollections = (enabled = true) =>
  useQuery({
    queryKey: ["stats", "collections", getCurrentUserKey()],
    queryFn: getDashboardCollections,
    staleTime: 60_000,
    enabled,
  });

const getClientCryptors = (): Promise<ClientCryptorCollectionEntity[]> =>
  unwrapApiResponse(
    statsAdminControllerClientCryptors<true>({ throwOnError: true }),
  );

export const useGetClientCryptors = (enabled = true) =>
  useQuery({
    queryKey: ["stats", "client-cryptors", getCurrentUserKey()],
    queryFn: getClientCryptors,
    staleTime: 60_000,
    enabled,
    retry: false,
  });

const getDashboardQrs = async (cityIds: number[]): Promise<QrOptionEntity[]> =>
  unwrapApiResponse(
    statsAdminControllerQrs<true>({
      query: {
        cityIds: cityIds.length > 0 ? cityIds.join(",") : undefined,
      },
      throwOnError: true,
    }),
  );

export const useGetDashboardQrs = (cityIds: number[]) =>
  useQuery({
    queryKey: [
      "stats",
      "qrs",
      getCurrentUserKey(),
      [...cityIds].sort((a, b) => a - b).join(","),
    ],
    queryFn: () => getDashboardQrs(cityIds),
    staleTime: 60_000,
    retry: false,
  });

const getComparisonKpis = async (): Promise<ComparisonKpis> =>
  unwrapApiResponse(
    statsAdminControllerComparison<true>({
      throwOnError: true,
    }),
  );

export const useGetComparisonKpis = (enabled = true) =>
  useQuery({
    queryKey: ["stats", "comparison", getCurrentUserKey()],
    queryFn: () => getComparisonKpis(),
    staleTime: 60_000,
    enabled,
    retry: false,
  });

export interface PeriodFilters {
  from: Date;
  to: Date;
  cityIds: number[];
  qrfyIds: number[];
}

const getPeriodKpis = async ({
  from,
  to,
  cityIds,
  qrfyIds,
}: PeriodFilters): Promise<PeriodKpis> =>
  unwrapApiResponse(
    statsAdminControllerPeriod<true>({
      query: {
        cityIds: cityIds.length > 0 ? cityIds.join(",") : undefined,
        qrfyIds: qrfyIds.length > 0 ? qrfyIds.join(",") : undefined,
        from: from.toISOString(),
        to: to.toISOString(),
      },
      throwOnError: true,
    }),
  );

export const useGetPeriodKpis = (
  filters: PeriodFilters | null,
  enabled = true,
) =>
  useQuery({
    queryKey: [
      "stats",
      "period",
      getCurrentUserKey(),
      filters?.from.toISOString(),
      filters?.to.toISOString(),
      [...(filters?.cityIds ?? [])].sort((a, b) => a - b).join(","),
      [...(filters?.qrfyIds ?? [])].sort((a, b) => a - b).join(","),
    ],
    queryFn: () => {
      if (!filters) throw new Error("Period filters are not initialized");
      return getPeriodKpis(filters);
    },
    staleTime: 60_000,
    enabled: enabled && filters !== null,
  });
